import React, { useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import { pieIcon, lineIcon, barIcon } from '@op/constants/chart';

import chartEvent, {
  ChartEventInjectedProps,
} from '@op/pages/Detail/component/chartEvent';
import CommonPieChart from './CommonPieChart';
import CommonLineChart from './CommonLineChart';
import { useOverviewStateChart } from '../hooks';
import useLanguage from '@op/hooks/useLanguage';
import { KeyMetricsItemType } from '../../constants';

type CommonChartProps = {
  metric: string;
  setWeeklyData?: (data: any) => void;
  metricItemRef: React.MutableRefObject<KeyMetricsItemType>;
};

function CreateChartComponent(type: 'channel' | 'language') {
  return function CommonChart({
    filter,
    chartType,
    setChartType,
    eventsDict,
    metric,
    setWeeklyData,
    metricItemRef
  }: ChartEventInjectedProps & CommonChartProps) {
    const { formatMessage } = useLanguage();

    const { operations } = useMemo(() => {
      let operations = [barIcon, pieIcon, lineIcon];
      const item = metricItemRef.current;

      if (item.isPercentage) {
        operations = operations.filter((item) => item.symbol === 'line');

        setChartType?.(operations[0].symbol);
      }

      return { operations };
    }, [metricItemRef.current]);

    const { lineData, pieData, loading, isEmpty } = useOverviewStateChart({
      type,
      filter,
      metric,
      chartType,
      setWeeklyData,
    });
    return (
      <CardCommon
        contentCard
        title={formatMessage(type === 'language' ? 'Language' : 'Channel')}
        loading={loading}
        empty={isEmpty}
        loadingHeight={338}
        defaultOperation={chartType}
        emptyHeight={338}
        operations={operations}
        operationInfo={[chartType!, setChartType!]}
      >
        <>
          {chartType === 'bar' && (
            <CommonLineChart
              lineData={lineData}
              type={type}
              filter={filter}
              chartType={'bar'}
              metricItem={metricItemRef.current}
              eventsDict={eventsDict}
            ></CommonLineChart>
          )}
          {chartType === 'pie' && (
            <CommonPieChart
              pieData={pieData}
              eventsDict={eventsDict}
              type={type}
              filter={filter}
              metricItem={metricItemRef.current}
            ></CommonPieChart>
          )}
          {chartType === 'line' && (
            <CommonLineChart
              lineData={lineData}
              type={type}
              filter={filter}
              chartType={'line'}
              metricItem={metricItemRef.current}
              eventsDict={eventsDict}
            ></CommonLineChart>
          )}
        </>
      </CardCommon>
    );
  };
}

function getFinalDealwithParams(
  type: 'channel' | 'language',
  newKey: 'exclude_channels' | 'except_language',
) {
  return function finalDealwithParams(params: any, options: any) {
    const { filter, chartType, event } = options;
    let obj = params;
    if (params?.[type]?.[0] === 'other') {
      const { data } = event;
      const legend = (data?.legend || []).filter(
        (item: string) => item !== 'other',
      );
      obj = Object.keys(params).reduce(
        (pre, cur) => {
          if (cur === type) {
            return pre;
          }
          return { ...pre, [cur]: params[cur] };
        },
        { [newKey]: legend } as any,
      );
    }

    if (chartType === 'pie') {
      return {
        ...obj,
        start_time: filter.start_time,
        end_time: filter.end_time,
        date_type: filter.date_type,
      };
    }
    return obj;
  };
}

export const ChannelChart = chartEvent<CommonChartProps>({
  key: 'WeeklyReportOverview',
  paramsList: {
    bar: { time: 'name', channel: 'seriesName' },
    pie: { channel: 'name' },
    line: { time: 'name', channel: 'seriesName' },
  },
  defaultChartType: 'bar',
  finalDealwithParams: getFinalDealwithParams('channel', 'exclude_channels'),
})(CreateChartComponent('channel'));

export const LanguageChart = chartEvent<CommonChartProps>({
  key: 'WeeklyReportOverview',
  paramsList: {
    bar: { time: 'name', language: 'seriesName' },
    pie: { language: 'name' },
    line: { time: 'name', language: 'seriesName' },
  },
  defaultChartType: 'bar',
  finalDealwithParams: getFinalDealwithParams('language', 'except_language'),
})(CreateChartComponent('language'));
